// FAC Custom extension for product-info
// This extends the default product-info.js to support fac-media-gallery

if (!customElements.get('fac-product-info')) {
  customElements.define(
    'fac-product-info',
    class FacProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "fac-product-info[id^='MainProduct'], product-info[id^='MainProduct']" : 'fac-product-info, product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('fac-product-info, product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getVariantFromUserSelections() {
        // Get the current data-selected-variant from the page
        const selectedVariantData = this.variantSelectors?.querySelector('[data-selected-variant]')?.innerHTML;
        if (!selectedVariantData || selectedVariantData === 'null') {
          return null;
        }

        // Check if all required options are selected by user
        const allFieldsets = this.variantSelectors.querySelectorAll('fieldset');
        const totalOptions = allFieldsets.length;
        let selectedOptions = 0;

        allFieldsets.forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) {
            selectedOptions++;
          }
        });

        // Only return variant if all options are selected
        if (selectedOptions === totalOptions && totalOptions > 0) {
          try {
            return JSON.parse(selectedVariantData);
          } catch (e) {
            return null;
          }
        }

        return null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Before replacing, preserve user selections and clear auto-selected options
          const currentSelections = {};
          this.variantSelectors.querySelectorAll('fieldset').forEach((fieldset) => {
            const checkedInput = fieldset.querySelector('input:checked');
            const optionName = fieldset.querySelector('input[type="radio"]')?.name;
            if (checkedInput && optionName) {
              currentSelections[optionName] = checkedInput.value;
            }
          });

          // Update the variant selects
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);

          // After update, restore only the user selections and clear others
          this.variantSelectors.querySelectorAll('fieldset').forEach((fieldset) => {
            const inputs = fieldset.querySelectorAll('input[type="radio"]');
            if (inputs.length === 0) return;

            const optionName = inputs[0].name;
            const userSelectedValue = currentSelections[optionName];

            inputs.forEach((input) => {
              if (userSelectedValue && input.value === userSelectedValue) {
                input.checked = true;
              } else if (!userSelectedValue) {
                // If user didn't select this option, uncheck it
                input.checked = false;
              }
            });
          });

          // Update the displayed selected value text
          this.variantSelectors.querySelectorAll('[data-selected-value]').forEach((el) => {
            const fieldset = el.closest('fieldset');
            const checkedInput = fieldset?.querySelector('input:checked');
            if (checkedInput) {
              el.textContent = checkedInput.value;
            } else {
              el.textContent = '';
            }
          });

          // Clear data-selected-variant if not all options are selected
          const allFieldsets = this.variantSelectors.querySelectorAll('fieldset');
          const checkedCount = Array.from(allFieldsets).filter((fs) => fs.querySelector('input:checked')).length;
          if (checkedCount < allFieldsets.length) {
            const selectedVariantEl = this.variantSelectors.querySelector('[data-selected-variant]');
            if (selectedVariantEl) {
              selectedVariantEl.textContent = 'null';
            }
          }
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // First update option values to preserve user selections
          this.updateOptionValues(html);

          // Then check if we have a valid variant based on user selections only
          const variant = this.getVariantFromUserSelections();

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          // Always update price, even when no variant is selected
          updateSourceFromDestination('price');

          // Update wishlist-engine (Liquid controls visibility based on variant selection)
          this.updateWishlistEngine(html);

          if (!variant) {
            this.setUnavailable(true);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable(isNoVariantSelected = false) {
        const message = isNoVariantSelected ? window.variantStrings.selectOptions : window.variantStrings.unavailable;
        this.productForm?.toggleSubmitButton(true, message);

        // When no variant is selected, keep price visible but hide other elements
        const selectorsToHide = isNoVariantSelected
          ? ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          : ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules'];

        const selectors = selectorsToHide
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector('fac-media-gallery, media-gallery');

        // Update main slider - use server HTML directly (trust Liquid template ordering)
        const mainSliderSource = this.querySelector('fac-media-gallery slider-component[id*="GalleryViewer"] ul[id^="Slider-Gallery"], media-gallery slider-component[id*="GalleryViewer"] ul[id^="Slider-Gallery"]');
        const mainSliderDestination = html.querySelector('fac-media-gallery slider-component[id*="GalleryViewer"] ul[id^="Slider-Gallery"], media-gallery slider-component[id*="GalleryViewer"] ul[id^="Slider-Gallery"]');
        const mainSliderComponent = this.querySelector('fac-media-gallery slider-component[id*="GalleryViewer"], media-gallery slider-component[id*="GalleryViewer"]');

        if (mainSliderSource && mainSliderDestination) {
          mainSliderSource.innerHTML = mainSliderDestination.innerHTML;
          // Reset slider component state (page count, button states, etc.)
          if (mainSliderComponent && mainSliderComponent.resetPages) {
            mainSliderComponent.resetPages();
          }
        }

        // Update thumbnail list - use server HTML directly (trust Liquid template ordering)
        const thumbnailListSource = this.querySelector('fac-media-gallery slider-component[id*="GalleryThumbnails"] ul[id^="Slider-Thumbnails"], media-gallery slider-component[id*="GalleryThumbnails"] ul[id^="Slider-Thumbnails"]');
        const thumbnailListDestination = html.querySelector('fac-media-gallery slider-component[id*="GalleryThumbnails"] ul[id^="Slider-Thumbnails"], media-gallery slider-component[id*="GalleryThumbnails"] ul[id^="Slider-Thumbnails"]');
        const thumbnailSliderComponent = this.querySelector('fac-media-gallery slider-component[id*="GalleryThumbnails"], media-gallery slider-component[id*="GalleryThumbnails"]');

        if (thumbnailListSource && thumbnailListDestination) {
          thumbnailListSource.innerHTML = thumbnailListDestination.innerHTML;
          // Reset thumbnail slider component state
          if (thumbnailSliderComponent && thumbnailSliderComponent.resetPages) {
            thumbnailSliderComponent.resetPages();
          }
        }

        // Re-enable zoom if needed
        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

        // Update variant label
        this.updateVariantLabel(html);

        // set featured media as active in the media gallery
        // Use prepend=false to preserve Liquid template ordering
        mediaGallery?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );

        // Update total media count in fac-media-gallery
        if (mediaGallery && mediaGallery.elements && mediaGallery.elements.viewer) {
          const actualSlides = mediaGallery.elements.viewer.querySelectorAll('[id^="Slide-"]');
          mediaGallery.totalMediaCount = actualSlides.length;
          if (mediaGallery.elements.counter && mediaGallery.elements.counter.total) {
            mediaGallery.elements.counter.total.textContent = actualSlides.length;
          }
        }

        // Update navigation arrows visibility
        if (mediaGallery && mediaGallery.updateNavArrowsVisibility) {
          mediaGallery.updateNavArrowsVisibility();
        }

        // Re-bind thumbnail click events after innerHTML replacement
        if (mediaGallery && mediaGallery.elements && mediaGallery.elements.thumbnails) {
          mediaGallery.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (button) {
              // Remove any existing listeners by cloning and replacing
              const newButton = button.cloneNode(true);
              button.parentNode.replaceChild(newButton, button);
              // Add new click listener
              newButton.addEventListener('click', mediaGallery.setActiveMedia.bind(mediaGallery, mediaToSwitch.dataset.target, false));
            }
          });

          // Re-bind mobile modal thumbnail events
          if (mediaGallery.setupMobileModalThumbnails) {
            mediaGallery.setupMobileModalThumbnails();
          }
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateVariantLabel(html) {
        const mediaLabelsSource = this.querySelector('.fac-gallery-media-labels');
        const mediaLabelsDestination = html.querySelector('.fac-gallery-media-labels');

        if (mediaLabelsSource && mediaLabelsDestination) {
          // サーバーから返されたHTMLでラベル群を更新
          // （バリアント変更によりcaptionやラベル内容が変わる可能性があるため）
          mediaLabelsSource.innerHTML = mediaLabelsDestination.innerHTML;

          // メディアギャラリーのラベル表示を更新
          const mediaGallery = this.querySelector('fac-media-gallery');
          if (mediaGallery && mediaGallery.updateMediaLabelVisibility) {
            const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
            if (viewer) {
              const activeSlide = viewer.querySelector('.is-active');
              if (activeSlide && activeSlide.dataset.mediaId) {
                mediaGallery.updateMediaLabelVisibility(activeSlide.dataset.mediaId);
              }
            }
          }
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      updateWishlistEngine(html) {
        const wishlistEngineSource = html.querySelector('.wishlist-engine');
        const wishlistEngineDestination = this.querySelector('.wishlist-engine');

        if (!wishlistEngineSource || !wishlistEngineDestination) return;

        // Get visibility states from data-visible attribute
        const currentlyVisible = wishlistEngineDestination.getAttribute('data-visible') === 'true';
        const shouldBeVisible = wishlistEngineSource.getAttribute('data-visible') === 'true';

        // Update variant_id attribute
        const newVariantId = wishlistEngineSource.getAttribute('data-variant_id');
        if (newVariantId) {
          wishlistEngineDestination.setAttribute('data-variant_id', newVariantId);
        }

        // Update data-visible attribute
        wishlistEngineDestination.setAttribute('data-visible', shouldBeVisible ? 'true' : 'false');

        // Only update styles if visibility state changes
        if (currentlyVisible !== shouldBeVisible) {
          if (shouldBeVisible) {
            // Fade in
            wishlistEngineDestination.style.opacity = '1';
            wishlistEngineDestination.style.visibility = 'visible';
            wishlistEngineDestination.style.pointerEvents = 'auto';
          } else {
            // Fade out
            wishlistEngineDestination.style.opacity = '0';
            wishlistEngineDestination.style.visibility = 'hidden';
            wishlistEngineDestination.style.pointerEvents = 'none';
          }
        }
        // If state doesn't change, keep current styles (no flicker)
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
